<template>
    <div class="fi-page">
        <div class="fi-header">
            <h2>{{ $t('invoices.title') }}</h2>
        </div>

        <!-- Filters -->
        <div class="fi-filters card">
            <div class="fi-filter-row">
                <div class="fi-field">
                    <label>ESIR</label>
                    <select v-model="selectedEsirId">
                        <option value="">{{ $t('invoices.selectEsir') }}</option>
                        <option v-for="e in esirs" :key="e.id" :value="e.id">
                            {{ e.locationName || e.esirCode }} ({{ e.posNumber || e.esirCode }})
                        </option>
                    </select>
                </div>
                <div class="fi-field fi-date">
                    <label>{{ $t('common.from') }}</label>
                    <el-date-picker v-model="filters.from" type="datetime" format="dd.MM.yyyy. HH:mm"
                        :placeholder="$t('common.from')" size="small" style="width:100%"></el-date-picker>
                </div>
                <div class="fi-field fi-date">
                    <label>{{ $t('common.to') }}</label>
                    <el-date-picker v-model="filters.to" type="datetime" format="dd.MM.yyyy. HH:mm"
                        :placeholder="$t('common.to')" size="small" style="width:100%"></el-date-picker>
                </div>
                <div class="fi-field">
                    <label>{{ $t('invoices.filters.invoiceType') }}</label>
                    <select v-model="filters.invoiceType">
                        <option value="">{{ $t('invoices.filters.all') }}</option>
                        <option value="Normal">{{ $t('pos.types.normal') }}</option>
                        <option value="Advance">{{ $t('pos.types.advance') }}</option>
                        <option value="Proforma">{{ $t('pos.types.proforma') }}</option>
                        <option value="Copy">{{ $t('pos.types.copy') }}</option>
                        <option value="Training">{{ $t('pos.types.training') }}</option>
                    </select>
                </div>
                <div class="fi-field">
                    <label>{{ $t('invoices.filters.transactionType') }}</label>
                    <select v-model="filters.transactionType">
                        <option value="">{{ $t('invoices.filters.allTypes') }}</option>
                        <option value="Sale">{{ $t('pos.txn.sale') }}</option>
                        <option value="Refund">{{ $t('pos.txn.refund') }}</option>
                    </select>
                </div>
            </div>
            <div class="fi-filter-row">
                <div class="fi-field">
                    <label>{{ $t('invoices.filters.pfrNumber') }}</label>
                    <input type="text" v-model="filters.pfrNumber" :placeholder="$t('common.search')">
                </div>
                <div class="fi-field">
                    <label>{{ $t('invoices.filters.buyerId') }}</label>
                    <input type="text" v-model="filters.buyerId" :placeholder="$t('pos.buyerIdPlaceholder')">
                </div>
                <div class="fi-field">
                    <label>{{ $t('invoices.filters.status') }}</label>
                    <select v-model="filters.status">
                        <option value="">{{ $t('invoices.filters.all') }}</option>
                        <option value="SUCCESS">{{ $t('invoices.filters.statusSuccess') }}</option>
                        <option value="ERROR">{{ $t('invoices.filters.statusError') }}</option>
                        <option value="PENDING">{{ $t('invoices.filters.statusPending') }}</option>
                    </select>
                </div>
                <div class="fi-field fi-summary">
                    <div class="fi-stat">{{ $t('invoices.filters.transactionType') }}: <strong>{{ totalCount }}</strong></div>
                    <div class="fi-stat">{{ $t('common.total') }}: <strong class="fi-total">{{ formatPrice(totalSales - totalRefunds) }}</strong></div>
                </div>
                <div class="fi-field fi-btn-field">
                    <button class="fi-btn-search" @click="currentPage = 1; loadInvoices()" :disabled="!selectedEsirId || loading">
                        <i class="tim-icons icon-zoom-split"></i> {{ loading ? 'Učitavanje...' : $t('common.search') }}
                    </button>
                    <button class="fi-btn-advance" @click="loadOpenAdvances()" :disabled="!selectedEsirId || loading">
                        <i class="tim-icons icon-coins"></i> {{ $t('invoices.openAdvances') || 'Otvoreni avansi' }}
                    </button>
                </div>
            </div>
        </div>

        <!-- Loading overlay -->
        <div v-if="loading" class="fi-loading card">
            <div class="fi-spinner"></div>
            <p>Učitavanje...</p>
        </div>

        <!-- Table -->
        <div class="fi-table-wrap card" v-if="selectedEsirId && !loading">
            <table class="fi-table">
                <thead>
                    <tr>
                        <th>#</th>
                        <th>{{ $t('invoices.table.date') }}</th>
                        <th>{{ $t('invoices.table.pfrNumber') }}</th>
                        <th>{{ $t('common.type') }}</th>
                        <th>{{ $t('invoices.table.transaction') }}</th>
                        <th>{{ $t('invoices.table.buyer') }}</th>
                        <th>{{ $t('invoices.table.amount') }}</th>
                        <th>{{ $t('invoices.table.tax') }}</th>
                        <th>{{ $t('invoices.table.status') }}</th>
                        <th>{{ $t('invoices.table.refNumber') }}</th>
                        <th></th>
                    </tr>
                </thead>
                <tbody>
                    <tr v-for="(inv, idx) in filtered" :key="inv.id" @click="openDetail(inv)" class="fi-row">
                        <td>{{ idx + 1 }}</td>
                        <td>{{ formatDate(inv.timeStamp) }}</td>
                        <td class="mono">{{ inv.pfrInvoiceNumber || '-' }}</td>
                        <td><span class="fi-tag fi-tag-type">{{ typeLabel(inv.invoiceType) }}</span></td>
                        <td><span :class="['fi-tag', inv.transactionType === 'Refund' ? 'fi-tag-refund' : 'fi-tag-sale']">{{ txnLabel(inv.transactionType) }}</span></td>
                        <td class="mono">{{ inv.buyerId || '-' }}</td>
                        <td class="mono fi-amount">{{ formatPrice(inv.totalAmount) }}</td>
                        <td class="mono">{{ formatPrice(inv.taxAmount) }}</td>
                        <td><span :class="['fi-status', 'fi-st-' + (inv.status || '').toLowerCase()]">{{ inv.status }}</span></td>
                        <td class="mono" style="font-size:10px">{{ inv.referentDocumentNumber || '' }}</td>
                        <td class="fi-actions">
                            <button v-if="canRefund(inv)" class="fi-btn fi-btn-ref" @click.stop="openRefund(inv)" :title="$t('pos.history.refundBtn')">
                                <i class="tim-icons icon-refresh-02"></i>
                            </button>
                            <button v-if="canStorno(inv)" class="fi-btn fi-btn-storno" @click.stop="doStorno(inv)" :title="$t('pos.history.stornoBtn')">
                                <i class="tim-icons icon-simple-remove"></i>
                            </button>
                            <button v-if="canFinal(inv)" class="fi-btn fi-btn-final" @click.stop="openFinal(inv)" title="Konačni račun">
                                <i class="tim-icons icon-check-2"></i>
                            </button>
                        </td>
                    </tr>
                    <tr v-if="filtered.length === 0"><td colspan="11" class="fi-empty">{{ $t('invoices.noInvoices') }}</td></tr>
                </tbody>
            </table>

            <!-- Paginacija -->
            <div v-if="totalCount > pageSize" class="fi-pagination">
                <button class="fi-page-btn" :disabled="currentPage <= 1" @click="changePage(currentPage - 1)">‹ Prethodna</button>
                <template v-for="p in paginationPages">
                    <span v-if="p === '...'" :key="'d'+p" class="fi-page-dots">...</span>
                    <button v-else :key="p" :class="['fi-page-btn', { active: p === currentPage }]" @click="changePage(p)">{{ p }}</button>
                </template>
                <button class="fi-page-btn" :disabled="currentPage >= totalPages" @click="changePage(currentPage + 1)">Sledeća ›</button>
                <span class="fi-page-info">{{ (currentPage-1)*pageSize + 1 }}–{{ Math.min(currentPage*pageSize, totalCount) }} od {{ totalCount }}</span>
            </div>
        </div>
        <div v-else class="fi-empty-state card">
            <i class="tim-icons icon-paper"></i>
            <p>{{ $t('invoices.selectEsirHint') }}</p>
        </div>

        <!-- Detail modal -->
        <transition name="modal-fade">
            <div v-if="detailInv" class="modal-overlay" @click="detailInv = null">
                <div class="modal-box detail-box" @click.stop>
                    <div class="det-header">
                        <h3>{{ $t('invoices.detail.title') }}: {{ detailInv.pfrInvoiceNumber || detailInv.id }}</h3>
                        <button @click="detailInv = null" class="det-close">&times;</button>
                    </div>
                    <div class="det-body">
                        <div class="det-journal" v-if="detailInv.journal">
                            <pre>{{ detailInv.journal }}</pre>
                        </div>
                        <div v-else class="det-info">
                            <div><strong>Tip:</strong> {{ typeLabel(detailInv.invoiceType) }} / {{ txnLabel(detailInv.transactionType) }}</div>
                            <div><strong>{{ $t('common.total') }}:</strong> {{ formatPrice(detailInv.totalAmount) }} RSD</div>
                            <div><strong>{{ $t('invoices.table.tax') }}:</strong> {{ formatPrice(detailInv.taxAmount) }} RSD</div>
                            <div v-if="detailInv.buyerId"><strong>{{ $t('invoices.table.buyer') }}:</strong> {{ detailInv.buyerId }}</div>
                            <div v-if="detailInv.pfrDateTime"><strong>PFR vreme:</strong> {{ detailInv.pfrDateTime }}</div>
                        </div>
                        <div class="det-link" v-if="detailInv.verificationUrl">
                            <a :href="detailInv.verificationUrl" target="_blank"><i class="tim-icons icon-link-72"></i> Verifikacioni link</a>
                        </div>
                    </div>
                </div>
            </div>
        </transition>

        <!-- Refund dialog -->
        <transition name="modal-fade">
            <div v-if="refDialog.show" class="modal-overlay" @click="refDialog.show = false">
                <div class="modal-box ref-box" @click.stop>
                    <div class="det-header" style="background:#1a73e8">
                        <h3><i class="tim-icons icon-refresh-02"></i> {{ $t('pos.refundDialog.title') }}</h3>
                        <button @click="refDialog.show = false" class="det-close">&times;</button>
                    </div>
                    <div class="det-body">
                        <div class="det-info">
                            <div><strong>PFR:</strong> {{ refDialog.inv ? refDialog.inv.pfrInvoiceNumber : '' }}</div>
                            <div><strong>{{ $t('common.amount') }}:</strong> {{ refDialog.inv ? formatPrice(refDialog.inv.totalAmount) : '' }} RSD</div>
                        </div>
                        <div style="margin-bottom:12px">
                            <label style="font-size:11px;font-weight:700;color:#495057;display:block;margin-bottom:4px">{{ $t('pos.refundDialog.buyerLabel') }} *</label>
                            <input type="text" v-model="refDialog.buyerId" :placeholder="$t('pos.buyerIdPlaceholder')" class="fi-input">
                        </div>
                        <div style="display:flex;gap:8px;justify-content:flex-end">
                            <button class="fi-btn-cancel" @click="refDialog.show = false">{{ $t('common.cancel') }}</button>
                            <button class="fi-btn-submit" @click="submitRefund()" :disabled="!refDialog.buyerId || refDialog.busy">
                                {{ refDialog.busy ? $t('pos.refundDialog.sending') : $t('pos.refundDialog.refundBtn') }}
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </transition>

        <!-- Konačni račun dialog -->
        <transition name="modal-fade">
            <div v-if="finalDialog.show" class="modal-overlay" @click="finalDialog.show = false">
                <div class="modal-box ref-box" @click.stop>
                    <div class="det-header" style="background:#28a745">
                        <h3><i class="tim-icons icon-check-2"></i> Konačni račun</h3>
                        <button @click="finalDialog.show = false" class="det-close">&times;</button>
                    </div>
                    <div class="det-body">
                        <div class="det-info">
                            <div><strong>Avans PFR:</strong> {{ finalDialog.inv ? finalDialog.inv.pfrInvoiceNumber : '' }}</div>
                            <div><strong>{{ $t('common.amount') }}:</strong> {{ finalDialog.inv ? formatPrice(finalDialog.inv.totalAmount) : '' }} RSD</div>
                            <div v-if="finalDialog.inv && finalDialog.inv.advanceDescription"><strong>Opis:</strong> {{ finalDialog.inv.advanceDescription }}</div>
                        </div>
                        <div style="margin-bottom:12px">
                            <label style="font-size:11px;font-weight:700;color:#495057;display:block;margin-bottom:4px">Način plaćanja</label>
                            <select v-model="finalDialog.paymentType" class="fi-input">
                                <option value="Cash">Gotovina</option>
                                <option value="Card">Kartica</option>
                                <option value="WireTransfer">Prenos na račun</option>
                                <option value="Other">Ostalo</option>
                            </select>
                        </div>
                        <div style="display:flex;gap:8px;justify-content:flex-end">
                            <button class="fi-btn-cancel" @click="finalDialog.show = false">{{ $t('common.cancel') }}</button>
                            <button class="fi-btn-submit" style="background:#28a745" @click="submitFinal()" :disabled="finalDialog.busy">
                                {{ finalDialog.busy ? 'Slanje...' : 'Izdaj konačni' }}
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </transition>

        <!-- Toast -->
        <transition name="notif-fade">
            <div v-if="toast.msg" :class="['fi-toast', toast.type]" @click="toast.msg = ''">
                <i :class="toast.type === 'success' ? 'tim-icons icon-check-2' : 'tim-icons icon-alert-circle-exc'"></i>
                <span>{{ toast.msg }}</span>
            </div>
        </transition>
    </div>
</template>

<script>
import axios from '@/util/axiosinstance'
import { DatePicker } from 'element-ui'

export default {
    name: 'FiscalInvoices',
    components: { 'el-date-picker': DatePicker },
    data() {
        const yesterday = new Date()
        yesterday.setDate(yesterday.getDate() - 1)
        const yFrom = new Date(yesterday); yFrom.setHours(0,0,0,0)
        const yTo = new Date(yesterday); yTo.setHours(23,59,59,0)
        return {
            esirs: [],
            selectedEsirId: '',
            invoices: [],
            filtered: [],
            filters: { from: yFrom, to: yTo, invoiceType: '', transactionType: '', pfrNumber: '', buyerId: '', status: '' },
            detailInv: null,
            refDialog: { show: false, inv: null, buyerId: '', busy: false },
            finalDialog: { show: false, inv: null, paymentType: 'Cash', busy: false },
            toast: { msg: '', type: 'success' },
            currentPage: 1,
            pageSize: 50,
            totalCount: 0,
            totalSales: 0,
            totalRefunds: 0,
            loading: false
        }
    },
    computed: {
        totalPages() {
            return Math.ceil(this.totalCount / this.pageSize) || 1
        },
        paginationPages() {
            const total = this.totalPages
            const cur = this.currentPage
            if (total <= 7) return Array.from({length: total}, (_, i) => i + 1)
            const pages = []
            pages.push(1)
            if (cur > 3) pages.push('...')
            for (let i = Math.max(2, cur - 1); i <= Math.min(total - 1, cur + 1); i++) pages.push(i)
            if (cur < total - 2) pages.push('...')
            pages.push(total)
            return pages
        },
        filteredTotal() {
            return this.filtered.reduce((s, i) => {
                const amount = Number(i.totalAmount || 0)
                return i.transactionType === 'Refund' ? s - amount : s + amount
            }, 0)
        }
    },
    created() {
        axios.get('/api/v1/esirs').then(r => { this.esirs = r.data })
    },
    methods: {
        loadInvoices() {
            if (!this.selectedEsirId) { this.invoices = []; this.filtered = []; this.totalCount = 0; return }
            this.loading = true
            const params = new URLSearchParams()
            const f = this.filters
            if (f.from) params.append('from', (f.from instanceof Date ? f.from.toISOString() : f.from))
            if (f.to) params.append('to', (f.to instanceof Date ? f.to.toISOString() : f.to))
            if (f.invoiceType) params.append('invoiceType', f.invoiceType)
            if (f.transactionType) params.append('transactionType', f.transactionType)
            if (f.buyerId) params.append('buyerId', f.buyerId)
            if (f.status) params.append('status', f.status)
            if (f.pfrNumber) params.append('pfrNumber', f.pfrNumber)
            params.append('limit', this.pageSize)
            params.append('offset', (this.currentPage - 1) * this.pageSize)

            const qs = params.toString()
            axios.get('/api/v1/invoices/esir/' + this.selectedEsirId + (qs ? '?' + qs : '')).then(r => {
                this.invoices = r.data
                this.filtered = r.data
                this.totalCount = parseInt(r.headers['x-total-count'] || r.data.length)
                this.totalSales = parseFloat(r.headers['x-total-sales'] || 0)
                this.totalRefunds = parseFloat(r.headers['x-total-refunds'] || 0)
            }).catch(e => {
                console.error('Load invoices failed:', e)
                this.showToast('Greška pri učitavanju faktura', 'error')
            }).finally(() => { this.loading = false })
        },
        changePage(page) {
            this.currentPage = page
            this.loadInvoices()
        },
        loadOpenAdvances() {
            if (!this.selectedEsirId) return
            axios.get('/api/v1/invoices/esir/' + this.selectedEsirId + '?openAdvances=true').then(r => {
                this.invoices = r.data
                this.filtered = r.data
            })
        },
        applyFilters() {
            let list = this.invoices
            const f = this.filters
            if (f.invoiceType) list = list.filter(i => i.invoiceType === f.invoiceType)
            if (f.transactionType) list = list.filter(i => i.transactionType === f.transactionType)
            if (f.status) list = list.filter(i => i.status === f.status)
            if (f.pfrNumber) { const q = f.pfrNumber.toLowerCase(); list = list.filter(i => (i.pfrInvoiceNumber||'').toLowerCase().includes(q)) }
            if (f.buyerId) { const q = f.buyerId.toLowerCase(); list = list.filter(i => (i.buyerId||'').toLowerCase().includes(q)) }
            if (f.from) { const t = f.from instanceof Date ? f.from.getTime() : new Date(f.from).getTime(); list = list.filter(i => this.toTs(i.timeStamp) >= t) }
            if (f.to) { const t = f.to instanceof Date ? f.to.getTime() : new Date(f.to).getTime(); list = list.filter(i => this.toTs(i.timeStamp) <= t) }
            this.filtered = list
        },
        toTs(d) {
            if (Array.isArray(d)) return new Date(d[0], d[1]-1, d[2], d[3]||0, d[4]||0, d[5]||0).getTime()
            return new Date(d).getTime()
        },
        typeLabel(t) { return { Normal: this.$t('pos.types.normal'), Advance: this.$t('pos.types.advance'), Proforma: this.$t('pos.types.proforma'), Copy: this.$t('pos.types.copy'), Training: this.$t('pos.types.training') }[t] || t },
        txnLabel(t) { return t === 'Sale' ? this.$t('pos.txn.sale') : t === 'Refund' ? this.$t('pos.txn.refund') : t },
        formatPrice(v) { return Number(v||0).toLocaleString('sr-RS', {minimumFractionDigits:2, maximumFractionDigits:2}) },
        formatDate(d) {
            if (!d) return '-'
            if (Array.isArray(d)) {
                const [y,m,day,h,min,s] = d
                return String(day).padStart(2,'0')+'.'+String(m).padStart(2,'0')+'.'+y+'. '+String(h||0).padStart(2,'0')+':'+String(min||0).padStart(2,'0')+':'+String(s||0).padStart(2,'0')
            }
            const dt = new Date(d); return isNaN(dt.getTime()) ? String(d) : dt.toLocaleString('sr-RS')
        },
        dateToStr(d) {
            if (!d) return null
            if (typeof d === 'string') return d
            if (d instanceof Date) return d.toISOString()
            if (Array.isArray(d)) {
                const [y, m, day, h, min, s] = d
                return String(y)+'-'+String(m).padStart(2,'0')+'-'+String(day).padStart(2,'0')+
                    'T'+String(h||0).padStart(2,'0')+':'+String(min||0).padStart(2,'0')+':'+String(s||0).padStart(2,'0')
            }
            return String(d)
        },
        canRefund(inv) {
            return inv.invoiceType === 'Normal' && inv.transactionType === 'Sale' && (inv.status === 'SUCCESS' || inv.status === 'CREATED')
        },
        canStorno(inv) {
            return inv.transactionType === 'Sale' && (inv.status === 'SUCCESS' || inv.status === 'CREATED')
        },
        canFinal(inv) {
            return inv.invoiceType === 'Advance' && inv.transactionType === 'Sale' && (inv.status === 'SUCCESS' || inv.status === 'CREATED')
        },
        openDetail(inv) {
            // Lista ne sadrži journal/responseBody — učitaj pun objekat za detail
            this.detailInv = inv
            axios.get('/api/v1/invoice/' + inv.id).then(r => {
                this.detailInv = r.data.invoice || r.data
            }).catch(() => { /* prikaži šta imamo */ })
        },
        openRefund(inv) { this.refDialog = { show: true, inv, buyerId: inv.buyerId || '', busy: false } },
        openFinal(inv) { this.finalDialog = { show: true, inv, paymentType: 'Cash', busy: false } },
        doStorno(inv) {
            if (!confirm(this.$t('pos.messages.stornoConfirm', { number: inv.pfrInvoiceNumber||'' }))) return
            axios.get('/api/v1/user/me').then(r => {
                this.refDialog = { show: false, inv, buyerId: r.data.clientTin || '', busy: false }
                this.submitRefund()
            }).catch(e => { this.showToast('Greška: ' + (e.message || ''), 'error') })
        },
        submitRefund() {
            const inv = this.refDialog.inv
            if (!inv || !this.refDialog.buyerId) return
            this.refDialog.busy = true
            axios.get('/api/v1/invoice/' + inv.id).then(res => {
                const items = (res.data.items||[]).map(it => ({
                    name: it.name, quantity: Number(it.quantity)||1, unitPrice: Number(it.unitPrice)||0,
                    label: it.label||'', taxRate: Number(it.taxRate)||0
                }))
                const total = items.reduce((s,i) => s + i.quantity*i.unitPrice, 0)
                const payments = (res.data.payments||[]).map(p => ({ paymentType: p.paymentType||'Cash', amount: Number(p.amount)||0 }))
                const payload = {
                    esirId: this.selectedEsirId, invoiceType: inv.invoiceType, transactionType: 'Refund',
                    buyerId: this.refDialog.buyerId,
                    referentDocumentNumber: inv.pfrInvoiceNumber || inv.invoiceNumber || '',
                    referentDocumentDT: this.dateToStr(inv.sdcDateTime || inv.pfrDateTime || ''),
                    items, payments: payments.length ? payments : [{ paymentType:'Cash', amount: total }]
                }
                axios.post('/api/v1/invoice', payload).then(r => {
                    this.refDialog.busy = false
                    if (r.data.status === 'OK') {
                        this.showToast(this.$t('pos.messages.refundSuccess') + ': ' + (r.data.pfrInvoiceNumber||''), 'success')
                        this.refDialog.show = false; this.loadInvoices()
                    } else { this.showToast(this.$t('pos.result.error') + ': ' + (r.data.error||''), 'error') }
                }).catch(e => { this.refDialog.busy = false; this.showToast(e.response?.data?.error||this.$t('pos.result.error'),'error') })
            })
        },
        submitFinal() {
            const inv = this.finalDialog.inv
            if (!inv) return
            this.finalDialog.busy = true
            axios.post('/api/v1/invoice/advance-final', {
                advanceId: inv.id,
                paymentType: this.finalDialog.paymentType
            }).then(r => {
                this.finalDialog.busy = false
                if (r.data.status === 'OK') {
                    const finalPfr = r.data.finalInvoice ? r.data.finalInvoice.pfrInvoiceNumber : ''
                    this.showToast('Konačni račun izdat: ' + finalPfr, 'success')
                    this.finalDialog.show = false
                    this.loadOpenAdvances()
                } else {
                    this.showToast('Greška: ' + (r.data.error || ''), 'error')
                }
            }).catch(e => {
                this.finalDialog.busy = false
                this.showToast(e.response?.data?.error || 'Greška pri izdavanju konačnog računa', 'error')
            })
        },
        showToast(msg, type) {
            if (this._tt) clearTimeout(this._tt)
            this.toast = { msg, type }
            this._tt = setTimeout(() => { this.toast = { msg:'', type:'success' } }, 4500)
        }
    }
}
</script>

<style scoped>
.fi-page { padding: 15px; }
.fi-header h2 { margin: 0 0 15px; font-size: 20px; color: #2c3e50; }
.card { background: #fff; border-radius: 10px; border: 1px solid #e0e0e0; margin-bottom: 15px; }
.fi-filters { padding: 12px 15px; }
.fi-filter-row { display: flex; gap: 10px; margin-bottom: 8px; flex-wrap: wrap; }
.fi-filter-row:last-child { margin-bottom: 0; }
.fi-field { flex: 1; min-width: 140px; }
.fi-field label { display: block; font-size: 10px; font-weight: 700; text-transform: uppercase; color: #6c757d; margin-bottom: 3px; }
.fi-field input, .fi-field select { width: 100%; padding: 7px 10px; border: 1px solid #dee2e6; border-radius: 6px; font-size: 13px; }
.fi-field input:focus, .fi-field select:focus { border-color: #1a73e8; outline: none; }
.fi-summary { display: flex; flex-direction: column; justify-content: flex-end; gap: 2px; }
.fi-stat { font-size: 12px; color: #6c757d; }
.fi-stat strong { color: #2c3e50; }
.fi-total { color: #28a745 !important; font-family: monospace; font-size: 14px; }
.fi-btn-field { display: flex; align-items: flex-end; }
.fi-btn-search {
    padding: 7px 20px; border: none; background: #1a73e8; color: #fff;
    border-radius: 6px; font-size: 13px; font-weight: 700; cursor: pointer;
    display: flex; align-items: center; gap: 6px; white-space: nowrap; transition: all 0.15s;
}
.fi-btn-search:hover { background: #1557b0; }
.fi-btn-search:disabled { opacity: 0.5; cursor: not-allowed; }
.fi-btn-advance {
    padding: 7px 14px; border: none; background: #e67e22; color: #fff;
    border-radius: 6px; font-size: 13px; font-weight: 700; cursor: pointer;
    display: flex; align-items: center; gap: 6px; white-space: nowrap; transition: all 0.15s;
}
.fi-btn-advance:hover { background: #d35400; }
.fi-btn-advance:disabled { opacity: 0.5; cursor: not-allowed; }
.fi-btn-field { gap: 6px; }
.fi-date .el-date-editor { width: 100% !important; }
.fi-table-wrap { overflow-x: auto; }
.fi-table { width: 100%; border-collapse: collapse; }
.fi-table th { padding: 8px 10px; font-size: 10px; font-weight: 700; text-transform: uppercase; color: #6c757d; background: #f8f9fa; border-bottom: 1px solid #e9ecef; text-align: left; white-space: nowrap; }
.fi-table td { padding: 7px 10px; border-bottom: 1px solid #f1f3f5; font-size: 12px; color: #2c3e50; }
.fi-row { cursor: pointer; transition: background 0.1s; }
.fi-row:hover { background: #f1f5ff; }
.mono { font-family: monospace; font-size: 11px; }
.fi-amount { font-weight: 700; }
.fi-tag { padding: 2px 6px; border-radius: 4px; font-size: 10px; font-weight: 700; }
.fi-tag-type { background: #e7f3ff; color: #1a73e8; }
.fi-tag-sale { background: #d4edda; color: #155724; }
.fi-tag-refund { background: #f8d7da; color: #dc3545; }
.fi-status { padding: 2px 6px; border-radius: 4px; font-size: 10px; font-weight: 700; }
.fi-st-success, .fi-st-created { background: #d4edda; color: #155724; }
.fi-st-error { background: #f8d7da; color: #721c24; }
.fi-st-pending { background: #fff3cd; color: #856404; }
.fi-empty { text-align: center; padding: 30px !important; color: #adb5bd; }
.fi-empty-state { text-align: center; padding: 60px 20px; color: #adb5bd; }
.fi-empty-state i { font-size: 40px; margin-bottom: 10px; }
.fi-actions { white-space: nowrap; }
.fi-btn { padding: 4px 6px; border: none; border-radius: 4px; cursor: pointer; font-size: 12px; margin-left: 2px; transition: all 0.15s; }
.fi-btn-ref { background: #e7f3ff; color: #1a73e8; }
.fi-btn-ref:hover { background: #1a73e8; color: #fff; }
.fi-btn-storno { background: #f8d7da; color: #dc3545; }
.fi-btn-storno:hover { background: #dc3545; color: #fff; }
.fi-btn-final { background: #d4edda; color: #28a745; }
.fi-btn-final:hover { background: #28a745; color: #fff; }
.fi-input { width: 100%; padding: 8px 10px; border: 1px solid #dee2e6; border-radius: 6px; font-size: 13px; }
.fi-input:focus { border-color: #1a73e8; outline: none; }
.fi-btn-cancel { padding: 8px 16px; border: 1px solid #dee2e6; background: #fff; border-radius: 6px; cursor: pointer; font-size: 13px; }
.fi-btn-submit { padding: 8px 16px; border: none; background: #1a73e8; color: #fff; border-radius: 6px; cursor: pointer; font-size: 13px; font-weight: 600; }
.fi-btn-submit:disabled { opacity: 0.5; cursor: not-allowed; }
/* Modal */
.modal-overlay { position: fixed; top: 0; left: 0; right: 0; bottom: 0; background: rgba(0,0,0,0.5); z-index: 10000; display: flex; align-items: center; justify-content: center; }
.modal-box { background: #fff; border-radius: 12px; overflow: hidden; width: 90%; box-shadow: 0 10px 40px rgba(0,0,0,0.2); }
.modal-fade-enter-active, .modal-fade-leave-active { transition: all 0.25s ease; }
.modal-fade-enter, .modal-fade-leave-to { opacity: 0; }
.detail-box { max-width: 550px; }
.ref-box { max-width: 420px; }
.det-header { padding: 15px 20px; background: #2c3e50; display: flex; justify-content: space-between; align-items: center; }
.det-header h3 { margin: 0; color: #fff; font-size: 16px; display: flex; align-items: center; gap: 8px; }
.det-close { background: none; border: none; color: #fff; font-size: 24px; cursor: pointer; }
.det-body { padding: 15px 20px; }
.det-journal { background: #f8f9fa; border: 1px solid #e9ecef; border-radius: 8px; padding: 12px; max-height: 400px; overflow-y: auto; }
.det-journal pre { margin: 0; font-family: 'Courier New', monospace; font-size: 11px; line-height: 1.4; color: #2c3e50; white-space: pre-wrap; }
.det-info { background: #f8f9fa; padding: 12px; border-radius: 8px; font-size: 13px; line-height: 1.8; margin-bottom: 12px; }
.det-link { text-align: center; margin-top: 12px; }
.det-link a { color: #1a73e8; font-weight: 600; text-decoration: none; font-size: 14px; }
.det-link a:hover { text-decoration: underline; }
/* Toast */
.fi-toast { position: fixed; top: 80px; left: 50%; transform: translateX(-50%); padding: 16px 32px; border-radius: 12px; display: flex; align-items: center; gap: 12px; font-size: 16px; font-weight: 600; z-index: 100000; cursor: pointer; box-shadow: 0 8px 30px rgba(0,0,0,0.25); color: #fff; max-width: 90vw; }
.fi-toast i { font-size: 22px; }
.fi-toast.success { background: #28a745; }
.fi-toast.error { background: #dc3545; }
.notif-fade-enter-active, .notif-fade-leave-active { transition: all 0.35s ease; }
.notif-fade-enter, .notif-fade-leave-to { opacity: 0; transform: translateX(-50%) translateY(-30px); }
.fi-pagination {
    display: flex; align-items: center; justify-content: center;
    gap: 4px; padding: 14px; border-top: 1px solid #e9ecef; flex-wrap: wrap;
}
.fi-page-btn {
    padding: 6px 12px; border: 1px solid #dee2e6; border-radius: 6px;
    background: #fff; color: #495057; font-size: 13px; cursor: pointer;
    transition: all 0.2s;
}
.fi-page-btn:hover:not(:disabled):not(.active) { background: #e9ecef; }
.fi-page-btn.active { background: #1a73e8; color: #fff; border-color: #1a73e8; }
.fi-page-btn:disabled { opacity: 0.4; cursor: default; }
.fi-page-dots { padding: 6px 4px; color: #6c757d; font-size: 13px; }
.fi-page-info { margin-left: 12px; color: #6c757d; font-size: 13px; }
/* Loading */
.fi-loading { text-align: center; padding: 60px 20px; }
.fi-loading p { color: #6c757d; font-size: 14px; margin-top: 15px; }
.fi-spinner {
    width: 36px; height: 36px; margin: 0 auto;
    border: 3px solid #e9ecef; border-top-color: #1a73e8;
    border-radius: 50%; animation: fi-spin 0.7s linear infinite;
}
@keyframes fi-spin { to { transform: rotate(360deg); } }
</style>